/**
 * Google Analytics 4 (GA4) integration for web.
 * Uses gtag.js as per https://developers.google.com/analytics/devguides/collection/ga4/web
 * Only runs when REACT_APP_GA_MEASUREMENT_ID is set in .env
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const GTAG_URL = "https://www.googletagmanager.com/gtag/js";

/** Current user id (set on login, cleared on logout). Sent with every event so you can filter by user immediately in Explore. */
let currentAppUserId = null;

/**
 * @returns {boolean} Whether GA is enabled (measurement ID configured)
 */
export function isAnalyticsEnabled() {
  return Boolean(MEASUREMENT_ID && MEASUREMENT_ID.startsWith("G-"));
}

/**
 * Get gtag function (after init). Safe to call even if GA is disabled.
 * @returns {function|undefined}
 */
function getGtag() {
  return typeof window !== "undefined" && window.gtag ? window.gtag : undefined;
}

/**
 * Load gtag script and initialize GA4 with the measurement ID.
 * For SPA we set send_page_view: false and send page_view manually on route change.
 * Call once at app bootstrap (e.g. in index.js).
 */
export function init() {
  if (!isAnalyticsEnabled()) return;

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());

  // Config: disable automatic page_view so we can send it manually on route change (SPA)
  const configOptions = { send_page_view: false };
  // Enable debug_mode in GA4 DebugView when REACT_APP_GA_DEBUG=true or ?ga_debug=1
  if (
    process.env.REACT_APP_GA_DEBUG === "true" ||
    (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("ga_debug") === "1")
  ) {
    configOptions.debug_mode = true;
  }
  gtag("config", MEASUREMENT_ID, configOptions);
}

/**
 * Load the gtag.js script dynamically. Call this after init() so config is already in dataLayer.
 * Script will use the existing dataLayer and config.
 */
export function loadGtagScript() {
  if (!isAnalyticsEnabled()) return;

  const id = MEASUREMENT_ID;
  const script = document.createElement("script");
  script.async = true;
  script.src = `${GTAG_URL}?id=${id}`;
  document.head.appendChild(script);
}

/**
 * Map path to a readable page-view event name for non-technical reports.
 * Order matters: more specific paths first.
 */
const PAGE_VIEW_EVENT_MAP = [
  { path: "/home", name: "Viewed home page" },
  { path: "/", name: "Viewed home page" },
  { path: "/tasks", name: "Viewed tasks page" },
  { path: "/stream", name: "Viewed stream page" },
  { path: "/queuetasks", name: "Viewed workspace tasks page" },
  { path: "/queue", name: "Viewed workspace page" },
  { path: "/workflow", name: "Viewed workflow page" },
  { path: "/organisationDashboard", name: "Viewed organisation dashboard page" },
  { path: "/taskHistory", name: "Viewed task history page" },
  { path: "/notification", name: "Viewed notifications page" },
  { path: "/comment", name: "Viewed comments page" },
  { path: "/tags", name: "Viewed tags page" },
  { path: "/reminder", name: "Viewed reminders page" },
  { path: "/activityDigest", name: "Viewed activity digest page" },
  { path: "/inbox", name: "Viewed inbox page" },
  { path: "/calendar", name: "Viewed calendar page" },
  { path: "/helpSupport", name: "Viewed help and support page" },
  { path: "/project", name: "Viewed projects page" },
  { path: "/email", name: "Viewed email page" },
  { path: "/cms", name: "Viewed content library page" },
  { path: "/library", name: "Viewed library page" },
  { path: "/teamSetting", name: "Viewed team settings page" },
  { path: "/automation", name: "Viewed automation page" },
  { path: "/userProfileRoute", name: "Viewed user profile page" },
  { path: "/userDirectory", name: "Viewed user directory page" },
  { path: "/taskSidebar", name: "Viewed task details" },
  { path: "/login", name: "Viewed login page" },
  { path: "/signup", name: "Viewed signup page" },
];

/**
 * Get readable event name for a path (e.g. "Viewed tasks page").
 * @param {string} path - pathname or full path
 * @returns {string}
 */
export function getPageViewEventName(path) {
  if (!path || typeof path !== "string") return "Viewed page";
  const pathname = path.split("?")[0];
  for (const { path: p, name } of PAGE_VIEW_EVENT_MAP) {
    if (pathname === p || pathname.startsWith(p + "/")) return name;
  }
  return "Viewed page";
}

/**
 * Send a page view with a readable event name (e.g. "Viewed tasks page") so reports are user-friendly.
 * @param {string} [path] - page path (default: window.location.pathname + search)
 * @param {string} [title] - page title (default: document.title)
 */
export function pageview(path, title) {
  const gtag = getGtag();
  if (!gtag || !isAnalyticsEnabled()) return;

  const pagePath = typeof path === "string" ? path : (typeof window !== "undefined" ? window.location.pathname + window.location.search : "");
  const pageLocation = typeof path === "string" ? window.location.origin + path : window.location.href;
  const pageTitle = typeof title === "string" ? title : (typeof document !== "undefined" ? document.title : "");
  const pageName = getPageViewEventName(pagePath);

  const params = withUserParam({
    page_location: pageLocation,
    page_title: pageTitle,
    page_path: pagePath,
    page_name: pageName,
  });
  gtag("event", pageName, params);
}


/**
 * Custom user property name for GA4. Use this when creating a Custom dimension in GA4:
 * Scope = User, User property = app_user_id (this name is allowed; "user_id" is reserved).
 */
export const GA_USER_PROPERTY_NAME = "app_user_id";

/**
 * Set the user ID for signed-in users (connects behavior across sessions/devices).
 * Also sets custom user property app_user_id so you can create a Custom dimension in GA4.
 * @param {string} userId - Your app's user identifier
 */
export function setUserId(userId) {
  const gtag = getGtag();
  if (!gtag || !isAnalyticsEnabled()) return;
  if (!userId) {
    clearUserId();
    return;
  }
  const id = String(userId);
  currentAppUserId = id;
  gtag("config", MEASUREMENT_ID, {
    user_id: id,
  });
  gtag("set", "user_properties", {
    [GA_USER_PROPERTY_NAME]: id,
  });
}

/**
 * Clear user ID on sign-out. Sends null as per GA docs and clears app_user_id.
 */
export function clearUserId() {
  const gtag = getGtag();
  if (!gtag || !isAnalyticsEnabled()) return;
  currentAppUserId = null;
  gtag("config", MEASUREMENT_ID, {
    user_id: null,
  });
  gtag("set", "user_properties", {
    [GA_USER_PROPERTY_NAME]: null,
  });
}

/** Merge app_user_id into event params so you can filter by user in GA4 Explore immediately (no 24h wait). */
function withUserParam(params = {}) {
  if (currentAppUserId == null) return params;
  return { ...params, app_user_id: currentAppUserId };
}

/**
 * Send a custom or recommended event to GA4.
 * @param {string} eventName - e.g. 'login', 'sign_up', 'page_view'
 * @param {Object} [params] - optional event parameters
 */
export function event(eventName, params = {}) {
  const gtag = getGtag();
  if (!gtag || !isAnalyticsEnabled()) return;
  gtag("event", eventName, withUserParam(params));
}

/**
 * Track when user logs in. Event name "User logged in" for readable reports.
 * @param {string} [method] - 'email', 'google', 'otp', etc.
 */
export function trackLogin(method = "email") {
  event("User logged in", { method });
}

/**
 * Track when user signs up.
 * @param {string} [method] - 'email', 'google', etc.
 */
export function trackSignUp(method = "email") {
  event("User signed up", { method });
}

/**
 * Track when user is redirected to home after login (or first landing).
 */
export function trackRedirectToHomePage() {
  event("Redirected to home page", {});
}

/**
 * Track when user clicks Add Task (opens new task panel).
 */
export function trackClickedAddTask() {
  event("Clicked add task", {});
}

/**
 * Track when user creates a new task.
 * @param {Object} [params] - optional: task_id, project_id, project_task_code
 */
export function trackAddTask(params = {}) {
  event("Created a new task", params);
}

/**
 * Track when user opens the comments section (task comments panel or comments page).
 * @param {Object} [params] - optional: task_id, context
 */
export function trackViewComments(params = {}) {
  event("Opened comment page", params);
}

/**
 * Track custom screen/section view.
 * @param {string} screenName - e.g. 'tasks', 'workflow'
 * @param {Object} [params] - optional extra params
 */
export function trackScreen(screenName, params = {}) {
  event("Viewed screen", { screen_name: screenName, ...params });
}

/**
 * Track when user deletes a task.
 */
export function trackDeleteTask(params = {}) {
  event("Deleted a task", params);
}

/**
 * Track when user updates a task (edit and save).
 */
export function trackUpdateTask(params = {}) {
  event("Updated a task", params);
}

/**
 * Track when user clones a task.
 */
export function trackCloneTask(params = {}) {
  event("Cloned a task", params);
}

/**
 * Track when user adds a comment.
 */
export function trackAddComment(params = {}) {
  event("Added a comment", params);
}

/**
 * Track when user deletes a comment or reply.
 */
export function trackDeleteComment(params = {}) {
  event("Deleted a comment", params);
}

/**
 * Track when user creates a new project.
 */
export function trackCreateProject(params = {}) {
  event("Created a new project", params);
}

/**
 * Track when user archives a task.
 */
export function trackArchiveTask(params = {}) {
  event("Archived a task", params);
}

/**
 * Track when user completes a task.
 */
export function trackCompleteTask(params = {}) {
  event("Completed a task", params);
}

/**
 * Track when user reopens a task.
 */
export function trackReopenTask(params = {}) {
  event("Reopened a task", params);
}

/**
 * Track when user retracts a task.
 */
export function trackRetractTask(params = {}) {
  event("Retracted a task", params);
}

/**
 * Track when user rejects a task.
 */
export function trackRejectTask(params = {}) {
  event("Rejected a task", params);
}

/**
 * Track when user accepts a task.
 */
export function trackAcceptTask(params = {}) {
  event("Accepted a task", params);
}

export default {
  isAnalyticsEnabled,
  init,
  loadGtagScript,
  pageview,
  getPageViewEventName,
  setUserId,
  clearUserId,
  GA_USER_PROPERTY_NAME,
  event,
  trackLogin,
  trackSignUp,
  trackRedirectToHomePage,
  trackClickedAddTask,
  trackAddTask,
  trackViewComments,
  trackScreen,
  trackDeleteTask,
  trackUpdateTask,
  trackCloneTask,
  trackAddComment,
  trackDeleteComment,
  trackCreateProject,
  trackArchiveTask,
  trackCompleteTask,
  trackReopenTask,
  trackRetractTask,
  trackRejectTask,
  trackAcceptTask,
};
